import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '../contexts/ThemeContext.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { ArrowLeft, Play, Zap, Target, Flame, Sun, Moon } from 'lucide-react'

function Hero() {
  const { theme, toggleTheme } = useTheme()
  const { language, t } = useLanguage()
  const [heroVideo, setHeroVideo] = useState(null)
  const [landingPhotos, setLandingPhotos] = useState([])
  const [showVideo, setShowVideo] = useState(false)

  useEffect(() => {
    const savedVideo = localStorage.getItem('oxygen_hero_video')
    if (savedVideo) {
      setHeroVideo(JSON.parse(savedVideo))
    }
    
    const savedPhotos = localStorage.getItem('oxygen_landing_photos')
    if (savedPhotos) {
      setLandingPhotos(JSON.parse(savedPhotos))
    }
  }, [])

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      {heroVideo && (
        <div className="absolute inset-0 z-0">
          <video 
            src={heroVideo.url} 
            autoPlay 
            muted 
            loop 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark/80 via-dark/50 to-dark" />
        </div>
      )}
      
      {/* Fallback Background */}
      {!heroVideo && (
        <div className="absolute inset-0 bg-gradient-to-br from-dark via-gray-900 to-dark">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,215,0,0.1),transparent_50%)]" />
        </div>
      )}

      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-20 w-72 h-72 bg-yellow-400 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-orange-500 rounded-full blur-[150px]" />
      </div>

      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-24 right-8 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
        title={theme === 'dark' ? 'Night Mode' : 'Dark Mode'}
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: language === 'ar' ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-gray-300">
                {language === 'ar' ? 'اكتشف قوتك الداخلية' : 'Discover Your Inner Strength'}
              </span>
            </motion.div>

            <motion.h1
              className="text-5xl md:text-7xl font-black leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className="text-white">{t('heroTitle1')} </span>
              <span className="text-gradient">{t('heroTitle2')}</span>
              <br />
              <span className="text-white">{t('heroTitle3')} </span>
              <span className="text-gradient">{t('heroTitle4')}</span>
            </motion.h1>

            <motion.p
              className="text-xl text-gray-400 mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {t('heroSubtitle')}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <a
                href="#offers"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-full hover:shadow-xl hover:shadow-yellow-400/25 transition-all duration-300"
              >
                {t('startJourney')}
                <ArrowLeft className={`w-5 h-5 transition-transform ${language === 'ar' ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1 rotate-180'}`} />
              </a>
              {heroVideo && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-full hover:bg-white/5 transition-all duration-300"
                >
                  <Play className="w-5 h-5" />
                  {t('watchVideo')}
                </button>
              )}
            </motion.div>

            <motion.div
              className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {[
                { icon: Target, value: '+500', label: t('activeMembers') },
                { icon: Flame, value: '50+', label: t('professionalTrainers') },
                { icon: Zap, value: '24/7', label: t('service24_7') },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {landingPhotos.length > 0 ? (
              <div className="relative aspect-square max-w-lg mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-3xl transform rotate-6" />
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/30 to-orange-500/30 rounded-3xl transform -rotate-3" />
                <div className="relative bg-gray-900 rounded-3xl overflow-hidden border border-white/10 aspect-square">
                  <img 
                    src={landingPhotos[0].image} 
                    alt={landingPhotos[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            ) : (
              <div className="relative aspect-square max-w-lg mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-3xl transform rotate-6" />
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/30 to-orange-500/30 rounded-3xl transform -rotate-3" />
                <div className="relative bg-gray-900 rounded-3xl overflow-hidden border border-white/10 aspect-square flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <Zap className="w-16 h-16 text-black" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">OXYGEN GYM</h3>
                    <p className="text-gray-400">{language === 'ar' ? 'قوة • لياقة • إرادة' : 'Strength • Fitness • Will'}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && heroVideo && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowVideo(false)}
        >
          <div className="relative max-w-5xl w-full aspect-video bg-gray-900 rounded-2xl overflow-hidden">
            <video 
              src={heroVideo.url} 
              controls 
              autoPlay
              className="w-full h-full"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              ✕
            </button>
          </div>
        </motion.div>
      )}
    </section>
  )
}

export default Hero
